import React from "react";
import {
  Box,
  Typography,
  Paper,
  useMediaQuery,
  CircularProgress,
  alpha,
} from "@mui/material";
import { ArrowForwardRounded, CheckRounded } from "@mui/icons-material";
import { evaluationModel } from "../../Models/evaluationModel";
import theme from "../../Constants/theme";
import colors from "../../Constants/colors";
import { useTranslation } from "react-i18next";
import { getModelIcon } from "../../Helpers/modelHelpers";

interface SelectEvaluationModelProps {
  selectedModelId: number | "";
  modelOptions: evaluationModel[];
  handleModelChange: (modelId: number) => void;
  isEvaluateLoading: boolean;
  useEvaluationModelLoading: boolean;
  onModelSelected?: () => void;
}

const SelectEvaluationModel: React.FC<SelectEvaluationModelProps> = ({
  selectedModelId,
  modelOptions,
  handleModelChange,
  useEvaluationModelLoading,
  onModelSelected,
}) => {
  const { t } = useTranslation("evaluationPage");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const filteredModels = modelOptions.filter(
    (model, index, self) =>
      index === self.findIndex((m) => m.model_id === model.model_id)
  );

  const handleModelClick = (modelId: number) => {
    handleModelChange(modelId);
    onModelSelected?.();
  };

  if (useEvaluationModelLoading) {
    return (
      <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 8,
          }}
        >
          <CircularProgress sx={{ color: colors.primary_dark }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", pt: 2, pb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          border: `1px solid ${colors.gray200}`,
          borderLeft: `4px solid ${colors.primary_dark}`,
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          sx={{
            fontWeight: 600,
            color: colors.primary_dark,
            marginBottom: 0,
          }}
        >
          {t("evaluationPage.selectEvaluationModel")}
        </Typography>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr",
            md: "repeat(2, 1fr)",
          },
          gap: 2,
          mt: 1,
        }}
      >
        {filteredModels.map((model, index) => {
          const isSelected = selectedModelId === model.model_id;
          return (
            <Paper
              key={`${model.model_id}-${index}`}
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                p: 2.5,
                borderRadius: 2,
                border: `2px solid ${
                  isSelected ? colors.accent : colors.gray200
                }`,
                borderLeft: `4px solid ${
                  isSelected ? colors.accent : colors.primary_dark
                }`,
                cursor: "pointer",
                backgroundColor: isSelected ? colors.orange50 : colors.white,
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: colors.accent,
                  borderLeftColor: colors.accent,
                  backgroundColor: isSelected
                    ? alpha(colors.accent, 0.7)
                    : alpha(colors.accent, 0.2),
                },
              }}
              onClick={() => handleModelClick(model.model_id)}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1.5,
                }}
              >
                {getModelIcon(model.model_name) && (
                  <Box
                    sx={{
                      width: 20,
                      height: 20,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={getModelIcon(model.model_name)!}
                      alt={model.model_name}
                      style={{
                        width: 20,
                        height: 20,
                        objectFit: "contain",
                        borderRadius: 0,
                      }}
                    />
                  </Box>
                )}
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary_dark,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {model.model_name}
                </Typography>
                <Box sx={{ flexShrink: 0 }}>
                  {isSelected ? (
                    <CheckRounded
                      sx={{
                        fontSize: 24,
                        color: colors.accent,
                      }}
                    />
                  ) : (
                    <ArrowForwardRounded
                      sx={{
                        fontSize: 20,
                        color: colors.gray400,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default SelectEvaluationModel;
